import React from 'react'
import { useCaleb } from '../context/CalebContext'

const TEAL      = '#0d9488'
const TEAL_DARK = '#0f766e'
const TEAL_DEEP = '#065f46'
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"

function friendlyError(raw) {
  if (!raw) return 'Could not load analysis. Please try again.'
  const s = String(raw).toLowerCase()
  if (s.includes('not found') || s.includes('404')) return 'The AI service is not yet deployed. Ask your clinic admin to complete the server setup.'
  if (s.includes('anthropic_api_key') || s.includes('not configured') || s.includes('503')) return 'The AI service is not configured yet. The clinic admin needs to set up the API key on the server.'
  if (s.includes('anthropic package') || s.includes('not installed')) return 'The AI package is not installed on the server yet. Ask your admin to run: pip install anthropic'
  if (s.includes('network') || s.includes('econnrefused')) return 'Cannot connect to the server. Please check your internet connection.'
  return raw.length > 200 ? raw.slice(0, 200) + '…' : raw
}

function AnalysisCard({ analysis }) {
  if (analysis.error) return (
    <div style={{ padding: '14px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px' }}>
      <div style={{ fontSize: '13px', fontWeight: '700', color: '#dc2626', marginBottom: '6px' }}>Caleb Unavailable</div>
      <div style={{ fontSize: '12px', color: '#7f1d1d', lineHeight: 1.6 }}>{friendlyError(analysis.error)}</div>
      <div style={{ fontSize: '11px', color: '#b91c1c', marginTop: '8px', padding: '6px 10px', background: '#fff5f5', borderRadius: '6px', border: '1px solid #fecaca' }}>
        You can still use the chat below to ask Caleb questions directly.
      </div>
    </div>
  )

  const sections = [
    { key: 'what_it_is',         label: 'What It Is' },
    { key: 'what_causes_it',     label: 'What Causes It' },
    { key: 'what_you_feel',      label: 'What You Feel' },
    { key: 'how_pt_helps',       label: 'How PT Helps' },
    { key: 'typical_timeline',   label: 'Typical Timeline' },
    { key: 'questions_to_ask',   label: 'Ask Your Therapist' },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {analysis.injury_name && (
        <div style={{ background: `linear-gradient(135deg, ${TEAL_DARK}, ${TEAL_DEEP})`, borderRadius: '10px', padding: '12px 14px' }}>
          <div style={{ fontSize: '15px', fontWeight: '700', color: 'white', lineHeight: 1.2 }}>{analysis.injury_name}</div>
          {analysis.body_region && <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', marginTop: '3px' }}>{analysis.body_region}</div>}
        </div>
      )}
      {sections.map(({ key, label }) => {
        const val = analysis[key]
        if (!val) return null
        const isArray = Array.isArray(val)
        return (
          <div key={key} style={{ background: '#f8fafc', borderRadius: '10px', padding: '12px 14px', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: TEAL, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
              {label}
            </div>
            {isArray ? (
              <ul style={{ margin: 0, paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                {val.map((item, i) => <li key={i} style={{ fontSize: '13px', color: '#334155', lineHeight: '1.55' }}>{item}</li>)}
              </ul>
            ) : (
              <div style={{ fontSize: '13px', color: '#334155', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{val}</div>
            )}
          </div>
        )
      })}
      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', padding: '10px 14px', fontSize: '12px', color: '#92400e', lineHeight: 1.6 }}>
        <strong>Note:</strong> {analysis.important_note || 'Caleb provides general guidance. Always follow your physical therapist\'s specific instructions for your treatment.'}
      </div>
    </div>
  )
}

export default function CalebPanel({ width = 480 }) {
  const { isOpen, close, context, history, input, setInput, loading, send, analysis, endRef } = useCaleb()

  if (!isOpen) return null

  const contextLabel = context?.injuryName
    ? `Re: ${context.injuryName}`
    : context?.exerciseName
    ? `Re: ${context.exerciseName}`
    : context?.patientName
    ? context.patientName
    : 'General'

  const placeholder = context?.injuryName
    ? `Ask about ${context.injuryName}…`
    : context?.exerciseName
    ? `Ask about ${context.exerciseName}…`
    : 'Ask Caleb anything… (Enter to send)'

  const emptyPrompts = context?.injuryName ? [
    `What causes ${context.injuryName}?`,
    `How long does recovery usually take?`,
    `What exercises will help me?`,
    `What should I avoid doing?`,
  ] : context?.exerciseName ? [
    `Why does this exercise help?`,
    `How do I know if I'm doing it right?`,
    `What muscles does this target?`,
    `Can I modify this if it hurts?`,
  ] : [
    'How am I progressing overall?',
    'What should I focus on this week?',
    'Can you write a SOAP note draft?',
  ]

  return (
    <>
    <style>{`
      .caleb-panel { width: ${width}px; }
      @media (max-width: 540px) { .caleb-panel { width: 100vw !important; } }
    `}</style>
    <div className="caleb-panel" style={{
      position: 'fixed', right: 0, top: 0, bottom: 0, zIndex: 300,
      background: 'white', boxShadow: '-6px 0 48px rgba(0,0,0,0.18)',
      display: 'flex', flexDirection: 'column', fontFamily: FONT,
    }}>
      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${TEAL_DARK} 0%, ${TEAL_DEEP} 100%)`, padding: '16px 18px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}></div>
            <div>
              <div style={{ fontSize: '15px', fontWeight: '700', color: 'white', letterSpacing: '-0.01em', lineHeight: 1 }}>Caleb</div>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>AI Clinical Assistant · BETA</div>
            </div>
          </div>
          <button onClick={close} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'rgba(255,255,255,0.7)', width: '28px', height: '28px', borderRadius: '50%', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>
        {contextLabel && (
          <div style={{ marginTop: '8px', fontSize: '11px', background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', padding: '4px 10px', borderRadius: '99px', display: 'inline-block' }}>
            {contextLabel}
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '18px 16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {/* Injury analysis card */}
        {analysis && <AnalysisCard analysis={analysis} />}

        {/* Loading analysis spinner */}
        {loading && !analysis && history.length === 0 && (
          <div style={{ textAlign: 'center', padding: '24px', color: '#94a3b8', fontSize: '13px' }}>
            <div style={{ fontSize: '20px', marginBottom: '8px', animation: 'spin 1s linear infinite', display: 'inline-block' }}></div>
            <div>Caleb is thinking…</div>
          </div>
        )}

        {/* Empty state prompts */}
        {!analysis && history.length === 0 && !loading && (
          <div style={{ padding: '8px 0' }}>
            <div style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '12px', fontWeight: '600' }}>Quick questions:</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {emptyPrompts.map(p => (
                <button key={p} onClick={() => { setInput(p); setTimeout(() => send(p), 0) }} style={{
                  padding: '11px 14px', background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '10px',
                  cursor: 'pointer', fontSize: '14px', color: '#334155', textAlign: 'left', fontFamily: FONT,
                  transition: 'all 0.12s',
                }}>{p}</button>
              ))}
            </div>
          </div>
        )}

        {/* Chat messages */}
        {history.map((msg, i) => (
          <div key={i} style={{
            maxWidth: '90%', alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
            background: msg.role === 'user' ? TEAL : '#f1f5f9',
            color: msg.role === 'user' ? 'white' : '#0f172a',
            padding: '12px 16px', borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
            fontSize: '15px', lineHeight: '1.65', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            boxShadow: msg.role === 'user' ? 'none' : '0 1px 4px rgba(0,0,0,0.06)',
          }}>{msg.content}</div>
        ))}

        {/* Typing indicator */}
        {loading && history.length > 0 && (
          <div style={{ alignSelf: 'flex-start', background: '#f1f5f9', padding: '10px 14px', borderRadius: '14px 14px 14px 4px', display: 'flex', gap: '4px', alignItems: 'center' }}>
            {[0,1,2].map(i => <div key={i} style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#94a3b8', animation: `bounce 1.2s ${i * 0.2}s infinite` }} />)}
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Disclaimer */}
      <div style={{ padding: '6px 14px', background: '#fffbeb', borderTop: '1px solid #fde68a', fontSize: '11px', color: '#92400e', lineHeight: 1.5, flexShrink: 0 }}>
        <strong>Note:</strong> Caleb provides general guidance only — your therapist has the final say.
      </div>

      {/* Input */}
      <div style={{ padding: '12px 16px', borderTop: '1.5px solid #e2e8f0', display: 'flex', gap: '10px', flexShrink: 0 }}>
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }}
          placeholder={placeholder}
          rows={3}
          style={{ flex: 1, padding: '11px 14px', fontSize: '15px', border: '1.5px solid #e2e8f0', borderRadius: '10px', resize: 'none', fontFamily: FONT, outline: 'none', lineHeight: 1.55, color: '#0f172a' }}
        />
        <button onClick={() => send()} disabled={loading || !input.trim()} style={{
          width: '44px', background: input.trim() ? TEAL : '#e2e8f0', color: input.trim() ? 'white' : '#94a3b8',
          border: 'none', borderRadius: '10px', cursor: input.trim() ? 'pointer' : 'default',
          fontSize: '20px', fontFamily: FONT, transition: 'all 0.15s', alignSelf: 'stretch', flexShrink: 0,
        }}>↑</button>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes bounce { 0%,80%,100% { transform: translateY(0); } 40% { transform: translateY(-6px); } }
      `}</style>
    </div>
    </>
  )
}
